'use client';
import { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Button from '@/components/ui/Button';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function CartPage() {
  const [cartItems, setCartItems] = useState([
    {
      id: 'item_1',
      name: 'Production Package - Professional',
      description: 'Full-scale video production with post-production',
      price: 15000,
      quantity: 1
    },
    {
      id: 'item_2',
      name: 'Brand Strategy Session',
      description: 'Comprehensive brand strategy consultation',
      price: 2500,
      quantity: 1
    }
  ]);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const updateQuantity = (id: string, delta: number) => {
    setCartItems(items =>
      items.map(item =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    );
  };

  const removeItem = (id: string) => {
    setCartItems(items => items.filter(item => item.id !== id));
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.0825; // 8.25% Nevada sales tax
  const total = subtotal + tax;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal">
              <SectionHeader
                eyebrow="Shopping Cart"
                title="Your Cart"
                description="Review your items before proceeding to checkout."
                align="center"
                className="mb-16"
              />
            </div>
          </div>
        </section>

        {/* Cart Content */}
        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto">
            {cartItems.length === 0 ? (
              <div className="reveal text-center py-16">
                <Icon name="ShoppingCartIcon" size={64} className="text-muted-foreground mx-auto mb-6" />
                <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
                <p className="text-body-sm text-muted-foreground mb-8">Add some services to get started.</p>
                <Link href="/services">
                  <Button variant="primary">Browse Services</Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* Cart Items */}
                <div className="lg:col-span-2 space-y-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="reveal p-6 border border-border">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2">{item.name}</h3>
                          <p className="text-body-sm text-muted-foreground">{item.description}</p>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-muted-foreground hover:text-primary transition-colors"
                          aria-label="Remove item"
                        >
                          <Icon name="XMarkIcon" size={20} />
                        </button>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-8 h-8 border border-border flex items-center justify-center hover:border-primary transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <Icon name="MinusIcon" size={16} />
                          </button>
                          <span className="text-body font-semibold w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-8 h-8 border border-border flex items-center justify-center hover:border-primary transition-colors"
                            aria-label="Increase quantity"
                          >
                            <Icon name="PlusIcon" size={16} />
                          </button>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold">${(item.price * item.quantity).toLocaleString()}</p>
                          <p className="text-micro uppercase tracking-widest text-muted-foreground">
                            ${item.price.toLocaleString()} each
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <div className="reveal p-8 border border-border sticky top-24">
                    <h3 className="text-xl font-bold mb-6">Order Summary</h3>
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between">
                        <span className="text-body-sm text-muted-foreground">Subtotal</span>
                        <span className="text-body font-semibold">${subtotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-body-sm text-muted-foreground">Tax (8.25%)</span>
                        <span className="text-body font-semibold">${tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="pt-4 border-t border-border">
                        <div className="flex justify-between">
                          <span className="text-body font-bold">Total</span>
                          <span className="text-2xl font-bold text-primary">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                    <Link href="/checkout">
                      <Button variant="primary" size="lg" className="w-full mb-4">
                        Proceed to Checkout
                      </Button>
                    </Link>
                    <Link href="/services">
                      <Button variant="secondary" size="md" className="w-full">
                        Continue Shopping
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}